import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { authService } from '../services/api';
import { clearSession, markWorkbenchRestoreAfterAuth } from '../utils/session';
import { prefetchDashboard, warmLoginShell } from '../utils/dashboardCache';
import { warmInvestigationCaches } from '../utils/warmInvestigationCaches';
import { exitGuestDemo } from '../utils/guestDemo';
import { OutrisSiteFooter } from '../components/OutrisSiteFooter';
import { routeAfterSignIn } from '../legal/postLoginRoute';
import {
    Lock,
    User,
    AlertCircle,
    Loader2,
    ShieldAlert,
    Building2,
    Moon,
    Sun,
    ArrowLeft,
} from 'lucide-react';
import { clsx } from 'clsx';
import { safeReturnPath } from '../utils/loginReturn';

// Outris portal base for the "Sign in with Outris" pull flow.
const PORTAL_BASE = (import.meta.env.VITE_PORTAL_URL || 'https://portal.outris.com').replace(/\/$/, '');

function readIsDarkTheme(): boolean {
    if (typeof document === 'undefined') return true;
    return document.documentElement.classList.contains('dark');
}

function applyTheme(isDark: boolean) {
    if (typeof document === 'undefined') return;
    if (isDark) {
        document.documentElement.classList.add('dark');
        localStorage.setItem('theme', 'dark');
    } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('theme', 'light');
    }
}

export function Login() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isDarkTheme, setIsDarkTheme] = useState(() => readIsDarkTheme());
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Detect if user was redirected here due to expired session
    const sessionExpired = location.state?.sessionExpired || searchParams.get('expired') === '1';
    const fromLocation = location.state?.from as { pathname?: string; search?: string } | undefined;
    const from = safeReturnPath(
        (fromLocation?.pathname
            ? `${fromLocation.pathname}${fromLocation.search || ''}`
            : null) || searchParams.get('next'),
        '/',
    );

    useEffect(() => {
        clearSession({ preservePublicCatalog: true });
        warmLoginShell();
    }, []);

    useEffect(() => {
        if (sessionExpired) markWorkbenchRestoreAfterAuth();
    }, [sessionExpired]);

    useEffect(() => {
        applyTheme(isDarkTheme);
    }, [isDarkTheme]);

    const toggleTheme = () => {
        setIsDarkTheme((prev) => !prev);
    };

    // Consent is NOT collected here. Both sign-in paths are gated after the
    // session exists by the server-recorded acceptance at /legal/accept
    // (SsoCallback / routeAfterSignIn below, and RequireAuth on every route).
    const handleSso = () => {
        window.location.href = `${PORTAL_BASE}/login?next=cryptoflow`;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append('username', username);
            formData.append('password', password);

            const data = await authService.login(formData);

            if (data.access_token) {
                localStorage.setItem('token', data.access_token);
                exitGuestDemo();
                if (data.user) {
                    localStorage.setItem('user', JSON.stringify(data.user));
                }
                await prefetchDashboard({ force: true });
                void warmInvestigationCaches();
                await routeAfterSignIn(navigate, from);
            }
        } catch (err: any) {
            console.error(err);
            if (err.response?.status === 401) {
                setError('Invalid username or password');
            } else {
                setError('Login failed. Please check your connection.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            className={clsx(
                'min-h-screen flex items-center justify-center p-4 pb-28 relative overflow-hidden',
                'bg-brand-page dark:bg-background text-foreground',
            )}
            data-testid="login-page"
        >
            {/* Top-Left: Back to Platform Overview */}
            <Link
                to="/landing"
                className={clsx(
                    'fixed top-5 left-5 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all shadow-xs',
                    'bg-card/90 backdrop-blur-md border-border text-muted-foreground hover:text-foreground hover:border-primary/40',
                )}
            >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Platform Overview</span>
            </Link>

            {/* Top-Right Theme Toggle */}
            <button
                type="button"
                onClick={toggleTheme}
                role="switch"
                aria-checked={isDarkTheme}
                aria-label={isDarkTheme ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                title={isDarkTheme ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                data-testid="login-theme-toggle"
                className={clsx(
                    'fixed top-5 right-5 z-20 inline-flex h-9 w-[3.6rem] items-center rounded-full border transition-colors shadow-xs',
                    'bg-card border-border',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                )}
            >
                {isDarkTheme ? (
                    <Sun className="absolute left-2 w-3.5 h-3.5 text-muted-foreground" />
                ) : (
                    <Moon className="absolute right-2 w-3.5 h-3.5 text-muted-foreground" />
                )}
                <span
                    className={clsx(
                        'absolute left-0.5 flex h-7 w-7 items-center justify-center rounded-full border border-border transition-transform duration-200 shadow-xs',
                        isDarkTheme
                            ? 'translate-x-7 bg-brand-ink ring-1 ring-border'
                            : 'translate-x-0 bg-brand-cream ring-1 ring-primary/30',
                    )}
                >
                    {isDarkTheme ? (
                        <Moon className="w-3.5 h-3.5 text-foreground" />
                    ) : (
                        <Sun className="w-3.5 h-3.5 text-brand-ink" />
                    )}
                </span>
            </button>

            {/* Login Card */}
            <div className="w-full max-w-md relative z-10">
                <div
                    className={clsx(
                        'relative rounded-xl overflow-hidden border border-border shadow-xl',
                        'bg-card text-card-foreground',
                    )}
                    data-testid="login-card"
                >
                    {/* Gold accent bar — brand primary yellow */}
                    <div
                        className="h-1 w-full bg-primary"
                        data-testid="login-card-accent"
                        aria-hidden="true"
                    />

                    <div className="p-8">
                        <div className="text-center mb-8">
                            <div
                                className={clsx(
                                    'inline-flex items-center justify-center mb-4 w-20 h-20 rounded-xl',
                                    'bg-white dark:bg-brand-ink',
                                    'ring-1 ring-primary/30 shadow-xs',
                                )}
                            >
                                <img
                                    src="/brand/cryptoflow-icon-light.svg"
                                    alt="CryptoFlow"
                                    className="w-14 h-14 block dark:hidden"
                                />
                                <img
                                    src="/brand/cryptoflow-icon-dark.svg"
                                    alt="CryptoFlow"
                                    className="w-14 h-14 hidden dark:block"
                                />
                            </div>
                            <h1 className="text-2xl font-bold mb-2 text-foreground tracking-tight">
                                CryptoFlow
                            </h1>
                            <p className="text-muted-foreground text-sm">
                                Crypto Intelligence &amp; Forensics Platform
                            </p>
                        </div>

                        {sessionExpired && !error && (
                            <div
                                className={clsx(
                                    'mb-5 p-3 rounded-lg flex items-center gap-2.5 text-sm',
                                    'bg-brand-cream/80 text-brand-ink border border-primary/40',
                                    'dark:bg-primary/10 dark:text-foreground dark:border-primary/30',
                                )}
                            >
                                <ShieldAlert className="w-4 h-4 shrink-0 text-primary" />
                                <span>Your session has expired. Please sign in again to continue.</span>
                            </div>
                        )}

                        {/* Primary path: Outris SSO — brand gold CTA */}
                        <button
                            type="button"
                            onClick={handleSso}
                            className={clsx(
                                'w-full font-semibold py-3.5 rounded-lg transition-colors',
                                'flex items-center justify-center gap-2.5 text-sm',
                                'bg-primary text-primary-foreground hover:bg-brand-gold-hover shadow-sm',
                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                                'disabled:opacity-50 disabled:cursor-not-allowed',
                            )}
                            data-testid="login-sso-button"
                        >
                            <Building2 className="w-5 h-5" />
                            Sign-in with Outris
                        </button>
                        <p className="mt-2 text-center text-xs text-muted-foreground">
                            Recommended — single sign-on for all Outris officers
                        </p>

                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-border" />
                            </div>
                            <div className="relative flex justify-center text-xs">
                                <span className="px-3 bg-card text-muted-foreground">
                                    or use your CryptoFlow ID
                                </span>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-500 text-sm animate-in fade-in slide-in-from-top-2">
                                    <AlertCircle className="w-4 h-4 shrink-0" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground/80 ml-1">Username</label>
                                    <div className="relative group">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                                        <input
                                            type="text"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            className={clsx(
                                                'w-full rounded-lg px-10 py-3 text-sm transition-colors',
                                                'bg-background border border-input text-foreground',
                                                'placeholder:text-muted-foreground',
                                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus:border-primary',
                                            )}
                                            placeholder="Enter officer ID"
                                            required
                                            autoComplete="username"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground/80 ml-1">Password</label>
                                    <div className="relative group">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                                        <input
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className={clsx(
                                                'w-full rounded-lg px-10 py-3 text-sm transition-colors',
                                                'bg-background border border-input text-foreground',
                                                'placeholder:text-muted-foreground',
                                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus:border-primary',
                                            )}
                                            placeholder="••••••••"
                                            required
                                            autoComplete="current-password"
                                        />
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className={clsx(
                                    'w-full font-medium py-3 rounded-lg text-sm transition-colors',
                                    'disabled:opacity-50 disabled:cursor-not-allowed',
                                    'flex items-center justify-center gap-2',
                                    'bg-brand-ink text-white hover:bg-neutral-900',
                                    'dark:bg-white dark:text-brand-ink dark:hover:bg-neutral-100',
                                )}
                                data-testid="login-password-submit"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Authenticating...
                                    </>
                                ) : (
                                    'Sign in'
                                )}
                            </button>
                        </form>

                        <p
                            className="mt-4 text-center text-xs text-muted-foreground"
                            data-testid="login-legal-notice"
                        >
                            Use of CryptoFlow is governed by the{' '}
                            <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>
                            {' '}and{' '}
                            <Link to="/terms" className="underline hover:text-foreground">Terms of Use</Link>
                            {' '}— you will be asked to accept them after signing in.
                        </p>

                        <div className="mt-8 pt-6 border-t border-border text-center">
                            <p className="text-xs text-muted-foreground">
                                Restricted Access • Authorized Law Enforcement Only
                                <br />
                                <span className="opacity-60">System Logs All Activity</span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <OutrisSiteFooter pinned />
        </div>
    );
}
